import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('xdx_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  auth0Callback: (data) => api.post('/auth/auth0-callback', data),
};

// Domains
export const domainAPI = {
  list: (params) => api.get('/domains', { params }),
  featured: () => api.get('/domains/featured'),
  categories: () => api.get('/domains/categories'),
  get: (id) => api.get(`/domains/${id}`),
  create: (data) => api.post('/domains', data),
  update: (id, data) => api.put(`/domains/${id}`, data),
  delete: (id) => api.delete(`/domains/${id}`),
};

// Auctions
export const auctionAPI = {
  list: (params) => api.get('/auctions', { params }),
  bid: (domainId, data) => api.post(`/auctions/${domainId}/bid`, data),
};

// User
export const userAPI = {
  dashboard: () => api.get('/user/dashboard'),
  domains: () => api.get('/user/domains'),
  bids: () => api.get('/user/bids'),
  transactions: () => api.get('/user/transactions'),
  updateProfile: (data) => api.put('/user/profile', data),
};

// Admin
export const adminAPI = {
  stats: () => api.get('/admin/stats'),
  users: () => api.get('/admin/users'),
  domains: (params) => api.get('/admin/domains', { params }),
  serviceRequests: () => api.get('/admin/service-requests'),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
};

// Services
export const serviceAPI = {
  valuation: (data) => api.post('/services/valuation', data),
  request: (data) => api.post('/services/request', data),
};

// Contact
export const contactAPI = {
  send: (data) => api.post('/contact', data),
};

// Chat
export const chatAPI = {
  send: (data) => api.post('/chat', data),
  history: (sessionId) => api.get(`/chat/history/${sessionId}`),
};

// Stats
export const statsAPI = {
  public: () => api.get('/stats'),
};

// Watchlist
export const watchlistAPI = {
  get: () => api.get('/watchlist'),
  getIds: () => api.get('/watchlist/ids'),
  add: (domainId) => api.post(`/watchlist/${domainId}`),
  remove: (domainId) => api.delete(`/watchlist/${domainId}`),
};

// Payments
export const paymentAPI = {
  createOrder: (data) => api.post('/payments/create-order', data),
  confirmPayment: (orderId, data) => api.post(`/payments/confirm/${orderId}`, data),
  getOrders: () => api.get('/payments/orders'),
};

// Blog
export const blogAPI = {
  list: (params) => api.get('/blog', { params }),
  get: (id) => api.get(`/blog/${id}`),
  create: (data) => api.post('/blog', data),
  update: (id, data) => api.put(`/blog/${id}`, data),
  delete: (id) => api.delete(`/blog/${id}`),
};

// Notifications
export const notificationAPI = {
  get: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

// NameSilo
export const namesiloAPI = {
  listDomains: () => api.get('/namesilo/list-domains'),
  importAuctions: (data) => api.post('/namesilo/import-auctions', data),
  domainInfo: (domain) => api.get(`/namesilo/domain-info/${domain}`),
};

// Stripe
export const stripeAPI = {
  createCheckout: (data) => api.post('/payments/stripe/create-checkout', data),
};

// Financing
export const financingAPI = {
  apply: (data) => api.post('/financing/apply', data),
  myApplications: () => api.get('/financing/my-applications'),
};

// Admin extended
export const adminExtAPI = {
  analytics: () => api.get('/admin/analytics'),
  settings: () => api.get('/admin/settings'),
  updateSettings: (key, data) => api.put(`/admin/settings/${key}`, data),
  emailLogs: (params) => api.get('/admin/email-logs', { params }),
  emailConfig: () => api.get('/admin/email-config'),
  financing: () => api.get('/admin/financing'),
  updateFinancing: (id, data) => api.put(`/admin/financing/${id}`, data),
  setValuation: (id, data) => api.put(`/admin/domains/${id}/valuation`, data),
  bulkValuate: () => api.post('/admin/domains/bulk-valuate'),
  toggleFeatured: (id, data) => api.put(`/admin/domains/${id}/featured`, data),
};

// Advanced Search
export const searchAPI = {
  advanced: (params) => api.get('/domains/search/advanced', { params }),
};

// Featured Auctions
export const featuredAPI = {
  auctions: (limit = 8) => api.get('/featured-auctions', { params: { limit } }),
};

// SEO
export const seoAPI = {
  sitemap: () => api.get('/seo/sitemap'),
  meta: (type, id) => api.get(`/seo/meta/${type}/${id}`),
};

// 2FA
export const twoFactorAPI = {
  setup: () => api.get('/auth/2fa/setup'),
  enable: (code) => api.post('/auth/2fa/enable', { code }),
  disable: (code) => api.post('/auth/2fa/disable', { code }),
  verify: (data) => api.post('/auth/2fa/verify', data),
  status: () => api.get('/auth/2fa/status'),
};

// Admin Extended - Freeze Users
export const adminUserAPI = {
  freezeUser: (userId, data) => api.post(`/admin/users/${userId}/freeze`, data),
  frozenUsers: () => api.get('/admin/frozen-users'),
};

// Admin Auction Controls
export const adminAuctionAPI = {
  list: (params) => api.get('/admin/auctions', { params }),
  extend: (domainId, data) => api.put(`/admin/auctions/${domainId}/extend`, data),
  endEarly: (domainId, data) => api.put(`/admin/auctions/${domainId}/end`, data),
  winners: (params) => api.get('/admin/auction-winners', { params }),
  secondChanceOffers: () => api.get('/admin/second-chance-offers'),
  extendPaymentDeadline: (domainId, data) => api.put(`/admin/auctions/${domainId}/payment-deadline`, data),
  runJobs: () => api.post('/admin/auctions/run-jobs'),
  logs: (params) => api.get('/admin/logs', { params }),
};

// User Second Chance Offers
export const secondChanceAPI = {
  myOffers: () => api.get('/user/second-chance-offers'),
  accept: (offerId) => api.post(`/user/second-chance-offers/${offerId}/accept`),
  decline: (offerId) => api.post(`/user/second-chance-offers/${offerId}/decline`),
};

// Analytics Config
export const analyticsAPI = {
  getConfig: () => api.get('/config/analytics'),
  updateConfig: (data) => api.put('/admin/config/analytics', data),
};

// Email
export const emailAPI = {
  sendTest: (data) => api.post('/admin/send-test-email', data),
  status: () => api.get('/email/status'),
};

// Domain Garden
export const gardenAPI = {
  submit: (data) => api.post('/garden/submit', data),
  myDomains: () => api.get('/garden/my-domains'),
  reclaim: (id) => api.post(`/garden/${id}/reclaim`),
  // Admin
  adminList: (params) => api.get('/admin/garden', { params }),
  adminUpdate: (id, data) => api.put(`/admin/garden/${id}`, data),
  adminStats: () => api.get('/admin/garden/stats'),
};

// Domain Brokerage
export const brokerageAPI = {
  submit: (data) => api.post('/brokerage/submit', data),
  myRequests: () => api.get('/brokerage/my-requests'),
  getRequest: (id) => api.get(`/brokerage/${id}`),
  // Admin
  adminList: (params) => api.get('/admin/brokerage', { params }),
  adminUpdate: (id, data) => api.put(`/admin/brokerage/${id}`, data),
  adminStats: () => api.get('/admin/brokerage/stats'),
};

// Financial Reports
export const financialAPI = {
  overview: () => api.get('/admin/financial/overview'),
  dailyRevenue: (days) => api.get('/admin/financial/revenue/daily', { params: { days } }),
  monthlyRevenue: (months) => api.get('/admin/financial/revenue/monthly', { params: { months } }),
  transactions: (params) => api.get('/admin/financial/transactions', { params }),
  orders: (params) => api.get('/admin/financial/orders', { params }),
  commissions: () => api.get('/admin/financial/commissions'),
  export: (params) => api.get('/admin/financial/export', { params }),
  projections: () => api.get('/admin/financial/projections'),
};

// Bulk Import
export const bulkImportAPI = {
  preview: (data) => api.post('/domains/bulk-import/preview', data),
  import: (data) => api.post('/domains/bulk-import', data),
};

// Donations
export const donationAPI = {
  submit: (data) => api.post('/donation/submit', data),
  myDonations: () => api.get('/donation/my-donations'),
  // Admin
  adminList: (params) => api.get('/admin/donations', { params }),
  adminUpdate: (id, data) => api.put(`/admin/donations/${id}`, data),
  adminStats: () => api.get('/admin/donations/stats'),
};

// GoDaddy
export const godaddyAPI = {
  appraisal: (domain) => api.get(`/godaddy/appraisal/${domain}`),
  checkAvailability: (domain) => api.get(`/godaddy/available/${domain}`),
  status: () => api.get('/godaddy/status'),
};

// Marketplace Analytics (public)
export const marketplaceAPI = {
  analytics: () => api.get('/marketplace/analytics'),
};

// Bug Reports
export const bugReportAPI = {
  submit: (data) => api.post('/bug-reports', data),
  myReports: () => api.get('/bug-reports/my-reports'),
  adminList: (params) => api.get('/admin/bug-reports', { params }),
  adminUpdate: (id, data) => api.put(`/admin/bug-reports/${id}`, data),
  adminStats: () => api.get('/admin/bug-reports/stats'),
};

export default api;
